import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import LocationPicker from '../components/common/LocationPicker';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import HoursInput from '../utils/HoursInput';

const PLACEHOLDER_IMAGE = "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fc1.staticflickr.com%2F9%2F8568%2F30436737336_4bc59f3a5c_b.jpg&f=1&nofb=1&ipt=01553680de38ac50186df9c76516d05329fadba76025609f0ade7763180cc1ee?auto=format&fit=crop&q=80&w=400";

function normalizeWebsite(url) {
  if (!url) return '';
  const v = url.trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (!v.includes(' ') && v.includes('.')) return 'https://' + v;
  return '';
}

// Helper to ensure structure is always valid
const DEFAULT_BUSINESS_HOURS = () => {
  const days = {};
  ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].forEach(d => {
    days[d] = { opening: '', closing: '', isOpen: false };
  });
  return { isOpen247: false, variableHours: false, ...days };
};

const EditListingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!!id);
  const [addressQuery, setAddressQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoryTypes = [
    { key: 'General', labelKey: 'map.general' },
    { key: 'Gastronomy', labelKey: 'map.gastronomy' },
    { key: 'Shops', labelKey: 'map.shops' },
    { key: 'Health', labelKey: 'map.health' },
    { key: 'Education', labelKey: 'map.education' },
    { key: 'Services', labelKey: 'map.services' },
    { key: 'Wellness', labelKey: 'map.wellness' },
    { key: 'Technical', labelKey: 'map.technical' },
    { key: 'Culture', labelKey: 'map.culture' },
  ];
  
  const [formData, setFormData] = useState({
    businessName: '',
    description: '',
    lat: '',
    lng: '',
    tier: 'General',
    website: '',      
    address: '',      
    businessHours: DEFAULT_BUSINESS_HOURS(),
    phone: '',
    email: '',
    preference: 'email',
    photoUrl: '' 
  });

  useEffect(() => {
    if (id) {
      fetchListingDetails();
    }
  }, [id]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchListingDetails = async () => {
    try {
      const res = await api.get(`/map/listing/${id}`);
      const found = res.data;
      
      if (found) {
        // FIX 1: Deep merge the businessHours to ensure isOpen247 exists 
        // even if the DB record is old/missing that field.
        const mergedHours = {
          ...DEFAULT_BUSINESS_HOURS(),
          ...(found.businessHours || {})
        };

        setFormData({
          _id: found._id,
          businessName: found.businessName || '',
          description: found.details?.description || '',
          website: found.details?.website || '',
          address: found.location?.address || '',
          businessHours: mergedHours, // Use the merged version
          lat: found.location?.coordinates[1] || '',
          lng: found.location?.coordinates[0] || '',
          tier: found.tier || 'General',
          phone: found.contact?.phone || '',
          email: found.contact?.email || '',
          preference: found.contact?.preference || 'email',
          photoUrl: found.photoUrl || '' 
        });
        
        if (found.location?.address) setAddressQuery(found.location.address);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('profile.service-profile.alerts.error-loading'));
      navigate('/provider/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
  };

  const handleMapClick = async (lat, lng) => {
    const address = await fetchAddressFromCoords(lat, lng);
    setFormData(prev => ({
      ...prev,
      lat: lat,
      lng: lng,
      address: address
    }));
  };

  const fetchAddressFromCoords = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      if (data && data.display_name) {
        setAddressQuery(data.display_name);
        return data.display_name;
      }
    } catch (err) {
      console.error("Reverse geocode failed", err);
    }
    return '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setIsDirty(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone && !formData.email) {
      toast(t('profile.service-profile.alerts.contact-recommended'), { icon: '⚠️', duration: 4000 });
    }
    setIsSubmitting(true);
    const data = new FormData();

    // FIX 2: Ensure businessHours is stringified properly for the multipart upload
    const payload = { ...formData, _id: id, website: normalizeWebsite(formData.website) };
    
    Object.keys(payload).forEach(key => {
      if (key === 'businessHours') {
        // Explicitly stringify the object
        data.append(key, JSON.stringify(payload[key]));
      } else if (typeof payload[key] === 'object' && payload[key] !== null) {
        data.append(key, JSON.stringify(payload[key]));
      } else {
        data.append(key, payload[key]);
      }
    });

    if (selectedFile) {
      data.append('image', selectedFile); 
    }

    try {
      await api.post('/map/save-listing', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setIsDirty(false);
      toast.success(t('profile.service-profile.alerts.changes-saved'));
      navigate(user.role === 'admin' ? '/admin/providers' : '/provider/my-listings');
    } catch (err) {
      console.error("Save Error:", err);
      toast.error(t('profile.service-profile.alerts.error-saving-profile'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressSearch = async () => {
    if (!addressQuery) return;
    setSearching(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}`);
      const data = await response.json();
      if (data && data.length > 0) {
        const firstResult = data[0];
        setFormData(prev => ({
          ...prev,
          lat: parseFloat(firstResult.lat),
          lng: parseFloat(firstResult.lon),
          address: firstResult.display_name
        }));
      } else {
        toast.error(t('profile.general.alerts.address-not-found'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('profile.general.alerts.address-search-error'));
    } finally {
      setSearching(false);
    }
  };

  if (loading) return <div className="p-10 text-center">{t('general.loading')}</div>;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg rounded-xl mt-10 border border-gray-100">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">
        {isNew ? t('profile.service-profile.title-create') : t('profile.service-profile.title')}
      </h1>
      <p className="text-gray-500 mb-6">
        {isNew ? t('profile.service-profile.subtitle-create') : t('profile.service-profile.subtitle')}
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">{t('profile.service-profile.business-name')}</label>
          <input 
            className="w-full border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" 
            value={formData.businessName}
            onChange={e => updateForm({ businessName: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2"> {t('map.category')} </label>
          <select 
            className="w-full border border-gray-300 p-2 rounded-lg bg-white"
            value={formData.tier}
            onChange={e => updateForm({ tier: e.target.value })}
          >
            <option value="">{t('general.select')}</option>
            {categoryTypes.map(({ key, labelKey }) => (
              <option key={key} value={key}>{t(labelKey)}</option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">{t('profile.service-profile.photo')}</label>
          <div className="mt-2 flex items-center gap-4">
            <div className="h-20 w-20 rounded-lg overflow-hidden bg-gray-100 border">
              <img 
                src={preview || formData.photoUrl || PLACEHOLDER_IMAGE} 
                className="h-full w-full object-cover" 
                alt="Preview"
              />
            </div>
            <div>
              <input type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
              <p className="text-xs text-gray-400 mt-1">{t('profile.service-profile.image-hint')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">{t('profile.service-profile.website')}</label>
            <input
              type="text"
              placeholder={t('map.website-placeholder')}
              className="w-full border border-gray-300 p-2 rounded-lg"
              value={formData.website}
              onChange={e => updateForm({ website: e.target.value })}
            />
          </div>
        </div>

        {/* This component must receive the full object including isOpen247 */}
        <HoursInput
          value={formData.businessHours}
          onChange={bh => setFormData(prev => ({ ...prev, businessHours: bh }))}
        />

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">{t('profile.service-profile.description')}</label>
          <textarea 
            className="w-full border border-gray-300 p-2 rounded-lg h-32 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
            value={formData.description}
            onChange={e => updateForm({ description: e.target.value })}
            required
          />
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
          <h3 className="font-bold text-gray-700">{t('map.contact')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input 
              type="tel" className="w-full border p-2 rounded" 
              placeholder={t('profile.service-profile.placeholder-phone')}
              value={formData.phone}
              onChange={e => updateForm({ phone: e.target.value })}
            />
            <input 
              type="email" className="w-full border p-2 rounded" 
              placeholder={t('profile.service-profile.placeholder-email')}
              value={formData.email}
              onChange={e => updateForm({ email: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-4 border-t pt-4 mt-4">
          <label className="block text-lg font-bold text-gray-800">{t('profile.location')}</label>
          <div className="flex gap-2">
            <input 
              type="text" className="flex-grow border border-gray-300 p-2 rounded-lg"
              placeholder={t('profile.service-profile.placeholder-location')}
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
            />
            <button type="button" onClick={handleAddressSearch} className="bg-gray-800 text-white px-4 py-2 rounded-lg">
              {searching ? t('general.searching') : t('general.find')}
            </button>
          </div>
          <LocationPicker lat={formData.lat} lng={formData.lng} onLocationSelect={handleMapClick} />
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full bg-[#1d5cab] hover:bg-[#2372bd] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg shadow-md transition-opacity">
          {isSubmitting ? '...' : isNew ? t('profile.service-profile.create-service') : t('general.save')}
        </button>
      </form>
    </div>
  );
};

export default EditListingPage;
